import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const circularProgressVariants = cva("", {
  variants: {
    size: {
      sm: "w-6 h-6",
      md: "w-8 h-8",
      lg: "w-12 h-12",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export type CircularProgressProps = {
  /**
   * The value of the progress pie chart, between 0 and 100.
   */
  value: number;
  className?: string;
  /**
   * The color of the progress indicator. Defaults to currentColor.
   */
  color?: string;
} & VariantProps<typeof circularProgressVariants>;

const cx = 50;
const cy = 50;
const innerRadius = 32;
const outerRadius = 45;
const strokeWidth = 10;

// For the pie effect: radius at half the desired fill area, stroke width = fill area
// Stroke extends inward and outward from the path, so this fills from center to innerRadius
const pieRadius = innerRadius / 2;
const pieCircumference = 2 * Math.PI * pieRadius;

export function CircularProgress({
  value,
  className,
  size,
  color = "currentColor",
}: CircularProgressProps) {
  const clampedValue = Math.max(0, Math.min(100, value));
  const combinedClassName = cn(circularProgressVariants({ size }), className);

  const arcOffset = pieCircumference - (clampedValue / 100) * pieCircumference;

  return (
    <svg
      viewBox="0 0 100 100"
      className={combinedClassName}
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Progress: ${clampedValue.toFixed(0)}%`}
    >
      {/* Outer ring */}
      <circle
        cx={cx}
        cy={cy}
        r={outerRadius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
      />

      {/* Progress pie fill */}
      <circle
        cx={cx}
        cy={cy}
        r={pieRadius}
        fill="none"
        stroke={color}
        strokeWidth={innerRadius}
        strokeDasharray={pieCircumference}
        strokeDashoffset={arcOffset}
        style={{
          transition: "stroke-dashoffset 300ms cubic-bezier(0.4, 0, 0.2, 1)",
          transform: "rotate(-90deg)",
          transformOrigin: "center",
        }}
      />
    </svg>
  );
}
